import { Link } from '@tanstack/react-router';

import { PanelEmpty, PanelError, PanelLoading } from '../../finance/components/panel-state';
import { t } from '../../../lib/i18n';
import { useAnalyticsUsers } from '../api';
import { formatKyivDateTime } from '../format';

/**
 * «Зареєстровані за останнім візитом» — who came back most recently, each a
 * link to the client card's «Відвідування» tab.
 */
export function UsersLastSeen() {
  const query = useAnalyticsUsers();
  const users = query.data?.pages.flatMap((page) => page.items) ?? [];

  if (query.isLoading) return <PanelLoading />;
  if (query.isError) return <PanelError error={query.error} fallbackKey="admin.analytics.failed" />;
  if (users.length === 0) return <PanelEmpty messageKey="admin.analytics.users.empty" />;

  return (
    <div className="flex flex-col gap-3">
      <ul className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface">
        {users.map((user) => (
          <li key={user.id} className="border-b border-cf-line-soft last:border-b-0" data-testid="last-seen-row">
            <Link
              to="/clients/$id"
              params={{ id: user.id }}
              search={{ tab: 'visits' }}
              className="flex flex-col gap-0.5 px-3.5 py-2.5 hover:bg-cf-surface-sunken lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:px-[18px]"
            >
              <span className="min-w-0 truncate text-[13px] font-semibold">
                {user.name?.trim() || user.email?.trim() || user.id}
              </span>
              {/* Two unbreakable phrases that wrap as wholes on a phone —
                  never «сесій за 30 д:» on one line and «7» on the next. */}
              <span className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[11px] text-cf-ink-subtle lg:shrink-0 lg:gap-x-2 lg:text-[12px]">
                <span className="whitespace-nowrap">
                  {t('admin.analytics.users.lastSeen', {
                    at: user.lastSeenAt ? formatKyivDateTime(user.lastSeenAt) : '—',
                  })}
                </span>
                <span className="hidden lg:inline" aria-hidden="true">
                  ·
                </span>
                <span className="whitespace-nowrap">
                  {t('admin.analytics.users.sessions30d', { count: user.sessions30d })}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {query.hasNextPage && (
        <button
          type="button"
          onClick={() => void query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
          className="h-10 self-center rounded-cf-md border border-cf-line bg-cf-surface px-4 text-[13px] font-semibold hover:border-cf-ink disabled:opacity-50"
        >
          {query.isFetchingNextPage ? t('common.loading.default') : t('admin.analytics.users.more')}
        </button>
      )}
    </div>
  );
}
